'use client';

import { useEffect } from 'react';

/**
 * Loads an external stylesheet (e.g. a per-tenant Google Fonts URL, or a self-hosted font
 * like Momo Signature) without blocking the initial render/TTFB. The <link> is part of the
 * component's own render output, so it's present in the *initial* server-rendered HTML and
 * the browser can start fetching (and discovering @font-face sources) while still parsing
 * that HTML, rather than only after the JS bundle loads, hydrates, and commits an effect.
 *
 * `media="print"` is the standard non-blocking preload/swap trick: the browser fetches the
 * stylesheet without applying (or waiting on) it for render, then something promotes it to
 * `media="all"` once it's actually ready.
 *
 * A previous version (c8cd4f9c, then "fixed" via next/script's `beforeInteractive` strategy)
 * got this swap wrong twice:
 *   1. A React `onLoad` prop on the <link> itself: React drops unrecognized lowercase event
 *      props, so it never even reached the DOM.
 *   2. A `next/script strategy="beforeInteractive"` swap script: this only works for the very
 *      first hard navigation. Next's own runtime (client/app-bootstrap.js) reads the
 *      `self.__next_s` queue these scripts push onto exactly *once*, synchronously, before the
 *      first hydration — there is no ongoing consumer after that. Every later mount of this
 *      component (a font picked in the page editor, a client-side route change to a page with
 *      a different combined Google Fonts URL) pushed onto a queue nobody was reading anymore,
 *      so the <link> stayed stuck on media="print" forever: fetched but never applied. That's
 *      exactly the "only a few fonts work, blocks don't react to changes" symptom.
 *
 * The actual fix needs to handle both timings correctly with one mechanism:
 *   - First hard load: the SSR-rendered <link> can finish fetching (and fire `load`) before
 *     this effect even runs, especially for a small, often browser-cached file — so on mount
 *     we synchronously check `link.sheet` (populated once the stylesheet has been fetched and
 *     parsed, regardless of whether its media currently matches) to catch an already-completed
 *     load instead of relying on catching a live event.
 *   - Any later mount/href change (editor font swap, SPA navigation to a page needing a
 *     different combined URL): a plain `useEffect` keyed on `href` re-runs the same check and,
 *     if not yet loaded, attaches a `load` listener — both paths converge on the same
 *     `applyIfLoaded`, so there's no gap where an event could be missed.
 *
 * The <noscript> fallback still covers clients that never run any script at all (JS disabled).
 *
 * The effect alone still waited for hydration: on a throttled phone that's ~3–4 s after the CSS
 * itself arrived, so the page painted with fallback fonts (and only the critical storefront CSS)
 * for seconds and then re-laid out at hydration — together with every hydration-time style change,
 * one big layout shift. DEFERRED_STYLESHEET_ACTIVATOR (deferredStylesheetActivator.ts — an inline script the root layout puts
 * first in <head>, with the CSP nonce) applies each deferred sheet the moment it loads, during
 * HTML parsing; the effect below stays as the path for client-side navigations and the editor.
 */
export function DeferredStylesheet({ href }: { href: string | null }) {
    const linkId = href ? `deferred-stylesheet-${href.replace(/[^a-zA-Z0-9]/g, '-')}` : null;

    useEffect(() => {
        if (!href || !linkId) return;
        const link = document.getElementById(linkId) as HTMLLinkElement | null;
        if (!link) return;

        const applyIfLoaded = () => {
            if (link.sheet) link.media = 'all';
        };
        applyIfLoaded();
        link.addEventListener('load', applyIfLoaded);
        return () => link.removeEventListener('load', applyIfLoaded);
    }, [href, linkId]);

    if (!href || !linkId) return null;

    return (
        <>
            <link rel="preload" as="style" href={href} />
            <link rel="stylesheet" href={href} media="print" id={linkId} data-deferred-stylesheet="" />
            <noscript>
                <link rel="stylesheet" href={href} />
            </noscript>
        </>
    );
}
